// client/src/pages/admin/ConciergeOnboardingNew.js
// ============================================
// CONCIERGE ONBOARDING — NEW BATCH PAGE
// Admin creates a new onboarding batch, selects wholesaler,
// email branding, notify toggle, and adds business records.
// ============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Trash2, Edit2 } from 'lucide-react';
import { adminOnboardingAPI } from '../../services/api';
import WholesalerSelector from '../../components/onboarding/WholesalerSelector';
import BatchRecordForm from '../../components/onboarding/BatchRecordForm';

const ConciergeOnboardingNew = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const [wholesalerId, setWholesalerId] = useState(null);
  const [wholesaler, setWholesaler] = useState(null);
  const [note, setNote] = useState('');
  const [notifyWholesaler, setNotifyWholesaler] = useState(false);
  const [emailBranding, setEmailBranding] = useState('wholesaler');
  const [records, setRecords] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const existingEmailsExceptEditing = records
    .filter((_, i) => i !== editingIndex)
    .map((r) => r.email);

  const handleAddRecord = (record) => {
    if (editingIndex != null) {
      setRecords((prev) => prev.map((r, i) => (i === editingIndex ? record : r)));
      setEditingIndex(null);
    } else {
      setRecords((prev) => [...prev, record]);
    }
  };

  const handleRemove = (index) => {
    setRecords((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
  };

  const handleSaveBatch = async () => {
    if (!wholesalerId) {
      toast.error(t('conciergeOnboarding.validation.pickWholesaler', 'Select a wholesaler first'));
      return;
    }
    if (records.length === 0) {
      toast.error(t('conciergeOnboarding.validation.needRecord', 'Add at least one business record'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        wholesalerId,
        note: note.trim() || undefined,
        notifyWholesaler,
        emailBranding,
        records
      };
      const res = await adminOnboardingAPI.createBatch(payload);
      const created = res.data?.batch || res.data?.data || res.data;
      toast.success(t('conciergeOnboarding.toasts.batchCreated', 'Batch created'));
      if (created?._id) {
        navigate(`/admin/onboarding/${created._id}`);
      } else {
        navigate('/admin/onboarding');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create batch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('conciergeOnboarding.newBatchTitle', 'New onboarding batch')}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('conciergeOnboarding.newBatchSubtitle', 'Pick a sponsoring wholesaler and add businesses to onboard.')}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('conciergeOnboarding.form.labels.wholesaler', 'Sponsoring wholesaler')}
            </label>
            <WholesalerSelector
              value={wholesalerId}
              onChange={(id, w) => {
                setWholesalerId(id);
                setWholesaler(w);
              }}
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('conciergeOnboarding.form.labels.note', 'Internal note (optional)')}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full rounded-md border-gray-300 px-3 py-2 border text-sm"
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifyWholesaler}
                onChange={(e) => setNotifyWholesaler(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              {t('conciergeOnboarding.toggles.notifyWholesaler', 'Notify wholesaler when complete')}
            </label>

            <fieldset className="flex items-center gap-4">
              <legend className="text-sm font-medium text-gray-700 mr-2">
                {t('conciergeOnboarding.form.labels.emailBranding', 'Email branding')}
              </legend>
              <label className="inline-flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="emailBranding"
                  value="wholesaler"
                  checked={emailBranding === 'wholesaler'}
                  onChange={() => setEmailBranding('wholesaler')}
                />
                {t('conciergeOnboarding.toggles.emailBrandingWholesaler', 'Wholesaler logo')}
              </label>
              <label className="inline-flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  name="emailBranding"
                  value="platform"
                  checked={emailBranding === 'platform'}
                  onChange={() => setEmailBranding('platform')}
                />
                {t('conciergeOnboarding.toggles.emailBrandingPlatform', 'WholesaleHub logo')}
              </label>
            </fieldset>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('conciergeOnboarding.form.recordsSectionTitle', 'Businesses to onboard')} ({records.length})
          </h2>

          {records.length > 0 && (
            <div className="overflow-hidden border rounded-md">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.businessName', 'Business')}</th>
                    <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.email', 'Email')}</th>
                    <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.vatNumber', 'VAT')}</th>
                    <th className="px-3 py-2 text-left">{t('conciergeOnboarding.form.labels.language', 'Language')}</th>
                    <th className="px-3 py-2 text-right">{t('conciergeOnboarding.listColumns.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {records.map((r, i) => (
                    <tr key={`${r.email}-${i}`}>
                      <td className="px-3 py-2">{r.businessName}</td>
                      <td className="px-3 py-2">{r.email}</td>
                      <td className="px-3 py-2">{r.vatNumber}</td>
                      <td className="px-3 py-2">{r.language}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleEdit(i)}
                          className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mr-3"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemove(i)}
                          className="inline-flex items-center text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <BatchRecordForm
            initialValue={editingIndex != null ? records[editingIndex] : null}
            onSubmit={handleAddRecord}
            onCancel={editingIndex != null ? () => setEditingIndex(null) : null}
            submitLabel={
              editingIndex != null
                ? t('conciergeOnboarding.form.updateRecord', 'Update record')
                : t('conciergeOnboarding.form.addRecord', 'Add record')
            }
            existingEmails={existingEmailsExceptEditing}
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/onboarding')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={submitting}
          >
            {t('conciergeOnboarding.form.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSaveBatch}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting
              ? t('conciergeOnboarding.form.saving', 'Saving…')
              : t('conciergeOnboarding.form.saveBatch', 'Save batch')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConciergeOnboardingNew;
